/**
 * A persistent toast utility that keeps a single toast element on screen
 * and updates its message in-place without creating new toast animations.
 */

type ToastVariant = 'success' | 'info' | 'warning' | 'error';

const VARIANT_COLORS: Record<ToastVariant, { bg: string; border: string; text: string }> = {
  success: { bg: '#eafbe7', border: '#47a53a', text: '#1e4d18' },
  info:    { bg: '#e8f4fd', border: '#2e75d6', text: '#1a3d6d' },
  warning: { bg: '#fef3cd', border: '#cc8b00', text: '#664600' },
  error:   { bg: '#fde8e8', border: '#d93025', text: '#6d1a1a' },
};

let toastElement: HTMLDivElement | null = null;
let messageElement: HTMLSpanElement | null = null;
let autoCloseTimer: ReturnType<typeof setTimeout> | null = null;

function getOrCreateToast(): { container: HTMLDivElement; message: HTMLSpanElement } {
  if (toastElement && messageElement && document.body.contains(toastElement)) {
    return { container: toastElement, message: messageElement };
  }

  const el = document.createElement('div');
  el.id = 'persistent-toast';
  el.style.cssText = `
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10000;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 12px 12px 20px;
    border-radius: 6px;
    border-left: 4px solid #2e75d6;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
    font-size: 14px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    max-width: 500px;
    min-width: 280px;
    text-align: left;
    transition: opacity 0.2s ease;
  `;

  const message = document.createElement('span');
  message.style.cssText = 'flex: 1;';
  el.appendChild(message);

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.setAttribute('aria-label', 'Dismiss');
  closeButton.textContent = '×';
  closeButton.style.cssText = `
    flex-shrink: 0;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 20px;
    line-height: 1;
    padding: 0 2px;
    color: inherit;
    opacity: 0.6;
  `;
  closeButton.onmouseenter = () => { closeButton.style.opacity = '1'; };
  closeButton.onmouseleave = () => { closeButton.style.opacity = '0.6'; };
  closeButton.onclick = () => closeToast();
  el.appendChild(closeButton);

  document.body.appendChild(el);
  toastElement = el;
  messageElement = message;
  return { container: el, message };
}

/**
 * Show or update the persistent toast with a new message and variant.
 * Only one toast exists at a time; calling this again just changes the text and color.
 */
export function updateToast(message: string, variant: ToastVariant = 'info'): void {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer);
    autoCloseTimer = null;
  }

  const { container, message: messageEl } = getOrCreateToast();
  const colors = VARIANT_COLORS[variant];
  container.style.backgroundColor = colors.bg;
  container.style.borderLeftColor = colors.border;
  container.style.color = colors.text;
  messageEl.textContent = message;
  container.style.opacity = '1';
}

/**
 * Show or update the toast, then auto-close it after the given duration (ms).
 */
export function showTemporaryToast(message: string, variant: ToastVariant = 'success', duration: number = 3000): void {
  updateToast(message, variant);

  autoCloseTimer = setTimeout(() => {
    closeToast();
  }, duration);
}

/**
 * Remove the persistent toast from the DOM.
 */
export function closeToast(): void {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer);
    autoCloseTimer = null;
  }

  if (toastElement && document.body.contains(toastElement)) {
    toastElement.style.opacity = '0';
    setTimeout(() => {
      if (toastElement && document.body.contains(toastElement)) {
        document.body.removeChild(toastElement);
      }
      toastElement = null;
      messageElement = null;
    }, 200);
  }
}
